import { Language } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { organizationService } from '../../application/OrganizationService';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

export interface UseRetrieveOrganizationUserInvitationProps {
  language: Language;
  email: string;
  firstName: string;
  lastName: string;
  organizationLabel: string;
}

type UseRetrieveOrganizationUserInvitationResponseProps = ServiceResponse<
  Function,
  UseRetrieveOrganizationUserInvitationProps
>;

// Hooks act as 'Adapter' layer.
export const useRetrieveOrganizationUserInvitation = ({
  token,
}: {
  token: string;
}): UseRetrieveOrganizationUserInvitationResponseProps => {
  const [invitationData, setInvitationData] = useState<UseRetrieveOrganizationUserInvitationProps | undefined>(
    undefined
  );
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, refetch } = useQuery(
    [`planner_user_invitation`, token],
    () => organizationService.fetchUserInvitation({ token }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (userInvitationData) => {
        setInvitationData(userInvitationData);
      },
      onError: (err: any) => {
        console.info(`Retrieve Organization User Invitation Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.get_invitation_fail`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    data: invitationData,
    run: refetch,
  };
};
